// I want this hook to combine onIncrement, onDecrement, onRemove into single functions that take only the deckCard id
import { findPairableCommander } from '@/lib/commanderPartners';
import { canHaveUnlimitedCopies, getMaxCopiesForFormat } from '@/lib/deckValidation';
import { fullCardCacheAtom } from '@/state/cardCache';
import type { DeckFormat, DeckZone } from '@/types/deck';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';
import { useBulkUpdateDeckCardZonesMutation, useRemoveDeckCardMutation, useUpdateDeckCardMutation } from './api';

/** Minimal card info needed for commander zone handling */
interface MinimalDeckCard {
  id: string;
  zone: DeckZone | string;
  scryfall_id: string;
}

export interface UseUpdateDeckCardParams {
  deckId: string;
  isLocked: boolean;
  deckFormat?: DeckFormat | null;
  /** All cards in the deck - required for commander zone handling */
  allCards?: MinimalDeckCard[];
}

export default function useDeckCardOperations({ deckId, isLocked, deckFormat, allCards }: UseUpdateDeckCardParams) {
  const updateMutation = useUpdateDeckCardMutation();
  const removeMutation = useRemoveDeckCardMutation();
  const moveMutation = useBulkUpdateDeckCardZonesMutation();
  const fullCardCache = useAtomValue(fullCardCacheAtom);

  /**
   * Check if a card can have its quantity increased.
   * Returns false if at max copies for format (e.g., 1 for commander, 4 for standard).
   * Always returns true for basic lands and "any number" cards.
   */
  const canIncrease = useCallback(
    (card: { name: string; oracle_text?: string | null; type_line?: string | null }, currentQuantity: number) => {
      if (isLocked) return false;
      if (canHaveUnlimitedCopies(card)) return true;
      const maxCopies = getMaxCopiesForFormat(deckFormat ?? null);
      return currentQuantity < maxCopies;
    },
    [isLocked, deckFormat],
  );

  const increase = useCallback(
    (cardId: string, currentQuantity: number) => {
      if (isLocked) return;
      updateMutation.mutate({
        deckId,
        cardId,
        data: { quantity: currentQuantity + 1 },
      });
    },
    [updateMutation, deckId, isLocked],
  );

  const decrease = useCallback(
    (cardId: string, currentQuantity: number) => {
      if (isLocked) return;
      if (currentQuantity <= 1) {
        // Remove card
        removeMutation.mutate({ deckId, cardId });
      } else {
        updateMutation.mutate({
          deckId: deckId,
          cardId,
          data: { quantity: currentQuantity - 1 },
        });
      }
    },
    [updateMutation, removeMutation, deckId, isLocked],
  );

  const remove = useCallback(
    (cardId: string) => {
      if (isLocked) return;
      removeMutation.mutate({ deckId, cardId });
    },
    [removeMutation, deckId, isLocked],
  );

  const changeZone = useCallback(
    (cardId: string, newZone: DeckZone) => {
      if (isLocked) return;

      const moves: { cardId: string; zone: DeckZone }[] = [];

      // Moving into the commander zone evicts the sitting commander(s), except one
      // that forms a legal partnership with the incoming card (Partner, Friends
      // forever, "Choose a Background" + Background, ...) so 2-commander decks work.
      if (newZone === 'commander' && allCards) {
        const currentCommanders = allCards.filter((c) => c.zone === 'commander' && c.id !== cardId);
        const incoming = allCards.find((c) => c.id === cardId);
        const incomingCard = incoming ? fullCardCache.get(incoming.scryfall_id) : undefined;

        const partner = incomingCard
          ? findPairableCommander(
              incomingCard,
              currentCommanders.map((entry) => ({ entry, card: fullCardCache.get(entry.scryfall_id) ?? null })),
            )
          : null;

        for (const commander of currentCommanders) {
          if (commander.id === partner?.entry.id) continue;
          moves.push({ cardId: commander.id, zone: 'main' });
        }
      }

      moves.push({ cardId, zone: newZone });

      // Routed through the merge-aware bulk mover so moving an edition into a zone
      // that already holds it merges quantities instead of creating a duplicate.
      moveMutation.mutate({ deckId, updates: moves });
    },
    [moveMutation, deckId, isLocked, allCards, fullCardCache],
  );

  const isLoading = updateMutation.isPending || removeMutation.isPending || moveMutation.isPending;

  return { increase, decrease, remove, changeZone, canIncrease, isLoading };
}
